<template>
  <div class="analytics">
    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-4 py-8">
      <!-- 维度与时间选择 -->
      <div class="flex flex-wrap gap-4 mb-6 items-center">
        <div>
          <label class="block text-gray-700 mb-1">分析维度</label>
          <select 
            v-model="selectedDimension" 
            class="border rounded-lg px-4 py-2"
            @change="updateChart"
          >
            <option value="income">收入</option>
            <option value="fans">粉丝增长</option>
            <option value="live">直播场次</option>
            <option value="gift">礼物收入</option>
            <option value="conversion">转化率</option>
            <option value="retention">观众留存率</option>
            <option value="engagement">互动数</option>
            <option value="duration">平均观看时长</option>
            <option value="platform">平台分布</option>
          </select>
        </div>
        <div>
          <label class="block text-gray-700 mb-1">时间范围</label>
          <select 
            v-model="selectedTimeRange" 
            class="border rounded-lg px-4 py-2"
            @change="updateChart"
          >
            <option value="7">近7天</option>
            <option value="30">近30天</option>
            <option value="90">近90天</option>
          </select>
        </div>
        <div>
          <label class="block text-gray-700 mb-1">图表类型</label>
          <div class="flex gap-2">
            <button 
              :class="[
                'chart-type-btn rounded px-3 py-2 font-bold',
                chartType === 'line' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
              ]"
              @click="setChartType('line')"
            >
              <i class="fas fa-chart-line"></i>
            </button>
            <button 
              :class="[
                'chart-type-btn rounded px-3 py-2 font-bold',
                chartType === 'bar' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
              ]"
              @click="setChartType('bar')"
            >
              <i class="fas fa-chart-bar"></i>
            </button>
            <button 
              :class="[
                'chart-type-btn rounded px-3 py-2 font-bold',
                chartType === 'pie' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
              ]"
              @click="setChartType('pie')"
            >
              <i class="fas fa-chart-pie"></i>
            </button>
          </div>
        </div>
        <button 
          class="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-indigo-700"
          @click="exportReport"
        >
          <i class="fas fa-download mr-2"></i>导出报表
        </button>
      </div>

      <!-- AI分析摘要 -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-8">
        <div class="flex justify-between items-center mb-2">
          <h3 class="text-lg font-bold flex items-center">
            <i class="fas fa-robot text-indigo-600 mr-2"></i>AI分析摘要
          </h3>
          <button 
            class="text-indigo-600 hover:underline flex items-center"
            @click="refreshAnalysis"
          >
            <i class="fas fa-sync-alt mr-1"></i>刷新
          </button>
        </div>
        <ul class="list-disc list-inside text-gray-700 space-y-1">
          <li>收入趋势呈现稳定增长态势，环比增长15.3%</li>
          <li>粉丝增长速度加快，新增粉丝转化率提升至8.5%</li>
          <li>直播场次频率适中，建议适当增加黄金时段直播</li>
          <li>礼物收入波动较大，需要优化礼物引导策略</li>
          <li>观众留存率表现良好，平均观看时长达到45分钟</li>
          <li>互动数据持续改善，建议增加更多互动环节</li>
        </ul>
      </div>

      <!-- 趋势图表 -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-8">
        <div class="flex items-center mb-4">
          <h3 class="text-lg font-bold flex items-center">
            <i class="fas fa-chart-line text-indigo-600 mr-2"></i>趋势图表
          </h3>
          <span class="ml-4 text-sm text-gray-500">
            {{ getChartDescription() }}
          </span>
        </div>
        <div class="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
          <div class="text-center">
            <i class="fas fa-chart-line text-4xl text-gray-400 mb-4"></i>
            <p class="text-gray-500">图表区域</p>
            <p class="text-sm text-gray-400 mt-2">
              当前维度：{{ getDimensionLabel() }} | 
              时间范围：近{{ selectedTimeRange }}天 | 
              图表类型：{{ getChartTypeLabel() }}
            </p>
          </div>
        </div>
      </div>

      <!-- 详细数据表格 -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-bold mb-4 flex items-center">
          <i class="fas fa-table text-indigo-600 mr-2"></i>详细数据
        </h3>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm text-gray-700">
            <thead>
              <tr class="border-b">
                <th class="text-left py-3 px-4 font-medium">日期</th>
                <th class="text-left py-3 px-4 font-medium">{{ getDimensionLabel() }}</th>
                <th class="text-left py-3 px-4 font-medium">变化率</th>
                <th class="text-left py-3 px-4 font-medium">趋势</th>
                <th class="text-left py-3 px-4 font-medium">备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in tableData" :key="index" class="border-b hover:bg-gray-50">
                <td class="py-3 px-4">{{ item.date }}</td>
                <td class="py-3 px-4 font-medium">{{ item.value }}</td>
                <td class="py-3 px-4">
                  <span :class="item.change >= 0 ? 'text-green-600' : 'text-red-600'">
                    {{ item.change >= 0 ? '+' : '' }}{{ item.change }}%
                  </span>
                </td>
                <td class="py-3 px-4">
                  <i :class="item.change >= 0 ? 'fas fa-arrow-up text-green-600' : 'fas fa-arrow-down text-red-600'"></i>
                </td>
                <td class="py-3 px-4 text-gray-500">{{ item.note }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'AnalyticsPage',
  data() {
    return {
      selectedDimension: 'income',
      selectedTimeRange: '30',
      chartType: 'line',
      tableData: [
        {
          date: '2024-03-21',
          value: '¥12,345',
          change: 15.3,
          note: '收入表现良好'
        },
        {
          date: '2024-03-20',
          value: '¥11,234',
          change: 8.7,
          note: '稳定增长'
        },
        {
          date: '2024-03-19',
          value: '¥10,567',
          change: -2.1,
          note: '略有下降'
        },
        {
          date: '2024-03-18',
          value: '¥10,789',
          change: 12.4,
          note: '恢复增长'
        },
        {
          date: '2024-03-17',
          value: '¥9,876',
          change: 5.2,
          note: '正常波动'
        }
      ]
    }
  },
  methods: {
    updateChart() {
      // 更新图表逻辑
      console.log('更新图表:', this.selectedDimension, this.selectedTimeRange)
    },
    setChartType(type) {
      this.chartType = type
      this.updateChart()
    },
    exportReport() {
      // 导出报表逻辑
      console.log('导出报表')
    },
    refreshAnalysis() {
      // 刷新分析逻辑
      console.log('刷新分析')
    },
    getDimensionLabel() {
      const labels = {
        income: '收入',
        fans: '粉丝数',
        live: '直播场次',
        gift: '礼物收入',
        conversion: '转化率',
        retention: '留存率',
        engagement: '互动数',
        duration: '观看时长',
        platform: '平台分布'
      }
      return labels[this.selectedDimension] || this.selectedDimension
    },
    getChartTypeLabel() {
      const labels = {
        line: '折线图',
        bar: '柱状图',
        pie: '饼图'
      }
      return labels[this.chartType] || this.chartType
    },
    getChartDescription() {
      return `显示近${this.selectedTimeRange}天${this.getDimensionLabel()}的变化趋势`
    }
  }
}
</script>

<style scoped>
.analytics {
  background: #f5f5f5;
  min-height: calc(100vh - 64px);
}

.flex {
  display: flex;
}

.flex-wrap {
  flex-wrap: wrap;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.gap-4 {
  gap: 1rem;
}

.gap-2 {
  gap: 0.5rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.ml-4 {
  margin-left: 1rem;
}

.p-6 {
  padding: 1.5rem;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.text-lg {
  font-size: 1.125rem;
}

.font-bold {
  font-weight: 700;
}

.font-medium {
  font-weight: 500;
}

.text-gray-500 {
  color: #6b7280;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-700 {
  color: #374151;
}

.text-indigo-600 {
  color: #4f46e5;
}

.text-green-600 {
  color: #16a34a;
}

.text-red-600 {
  color: #dc2626;
}

.text-white {
  color: #ffffff;
}

.bg-white {
  background-color: #ffffff;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.bg-gray-200 {
  background-color: #e5e7eb;
}

.bg-indigo-600 {
  background-color: #4f46e5;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded {
  border-radius: 0.25rem;
}

.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.border {
  border-width: 1px;
}

.border-b {
  border-bottom-width: 1px;
}

.h-80 {
  height: 20rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-4xl {
  font-size: 2.25rem;
}

.block {
  display: block;
}

.flex {
  display: flex;
}

.overflow-x-auto {
  overflow-x: auto;
}

.min-w-full {
  min-width: 100%;
}

.text-left {
  text-align: left;
}

.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

.hover\:bg-indigo-700:hover {
  background-color: #4338ca;
}

.hover\:underline:hover {
  text-decoration: underline;
}

.list-disc {
  list-style-type: disc;
}

.list-inside {
  list-style-position: inside;
}

.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.chart-type-btn {
  transition: all 0.2s ease;
}

.chart-type-btn:hover {
  transform: translateY(-1px);
}
</style> 